import {
  createTableDefinition,
  type TableDefinition,
} from '@/features/shared/components/tableSystem/tableDefinition';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import type { TestRunStatus } from '@flowqa/shared';

export type TestSortDirection = 'asc' | 'desc';
export type TestSortKey =
  | 'name'
  | 'displayName'
  | 'suiteId'
  | 'latestRunTimestamp'
  | 'executionPassRate'
  | 'executionFrequencyPerDay'
  | 'executionFlakeScore'
  | 'executionAverageDurationMs'
  | 'executionPassRateDelta'
  | 'executionFrequencyPerDayDelta'
  | 'executionFlakeScoreDelta'
  | 'executionAverageDurationMsDelta';

const testDefaultSortKey: TestSortKey = 'latestRunTimestamp';
const testDefaultSortDirection: TestSortDirection = 'desc';

const testSortOptions: Array<{ key: TestSortKey; label: string }> = [
  { key: 'name', label: 'Test' },
  { key: 'displayName', label: 'Display Name' },
  { key: 'suiteId', label: 'Suite' },
  { key: 'latestRunTimestamp', label: 'Latest' },
  { key: 'executionPassRate', label: getChartTitle('passRate') },
  { key: 'executionPassRateDelta', label: 'Success Rate Delta' },
  { key: 'executionFrequencyPerDay', label: getChartTitle('frequency') },
  { key: 'executionFrequencyPerDayDelta', label: 'Frequency Delta' },
  { key: 'executionAverageDurationMs', label: getChartTitle('avgDuration') },
  { key: 'executionAverageDurationMsDelta', label: 'Avg. Duration Delta' },
  { key: 'executionFlakeScore', label: getChartTitle('flakeScore') },
  { key: 'executionFlakeScoreDelta', label: 'Flake Score Delta' },
];

const testQuickSortOptions: Array<{
  key: TestSortKey;
  label: string;
  metric: 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore';
  firstDirection?: 'asc' | 'desc';
}> = [
  { key: 'executionPassRate', label: getChartTitle('passRate'), metric: 'passRate' },
  { key: 'executionFrequencyPerDay', label: getChartTitle('frequency'), metric: 'frequency' },
  { key: 'executionAverageDurationMs', label: getChartTitle('avgDuration'), metric: 'avgDuration' },
  {
    key: 'executionFlakeScore',
    label: getChartTitle('flakeScore'),
    metric: 'flakeScore',
    firstDirection: 'asc',
  },
];

const testSortFieldMap: Record<TestSortKey, string> = {
  name: 'name',
  displayName: 'displayName',
  suiteId: 'slug',
  latestRunTimestamp: 'latestRunTimestamp',
  executionPassRate: 'executionPassRate',
  executionFrequencyPerDay: 'executionFrequencyPerDay',
  executionFlakeScore: 'executionFlakeScore',
  executionAverageDurationMs: 'executionAverageDurationMs',
  executionPassRateDelta: 'executionPassRateDelta',
  executionFrequencyPerDayDelta: 'executionFrequencyPerDayDelta',
  executionFlakeScoreDelta: 'executionFlakeScoreDelta',
  executionAverageDurationMsDelta: 'executionAverageDurationMsDelta',
};

const testStatusOptions: Array<{ value: TestRunStatus; label: string }> = [
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'errored', label: 'Errored' },
];

export const testsTableDefinition: TableDefinition<TestSortKey, TestRunStatus> =
  createTableDefinition({
    title: 'Tests',
    searchAriaLabel: 'Search tests, suites, tags',
    columns: [
      { field: 'name', label: 'Test', mandatory: true, defaultVisible: true, userVisible: true },
      { field: 'suiteId', label: 'Suite', defaultVisible: false, userVisible: false },
      {
        field: 'statusFilter',
        label: getDetailLabel('status'),
        defaultVisible: false,
        userVisible: false,
      },
      {
        field: 'latestRunStatus',
        label: getChartTitle('latestStatus'),
        defaultVisible: true,
        userVisible: true,
      },
      {
        field: 'executionStatusCounts',
        label: 'Runs',
        defaultVisible: true,
        userVisible: true,
      },
      {
        field: 'latestRunDurationMs',
        label: 'Latest Duration',
        defaultVisible: false,
        userVisible: false,
      },
      {
        field: 'latestRunTimestamp',
        label: 'Latest Timestamp',
        defaultVisible: false,
        userVisible: false,
      },
      {
        field: 'metricsSummary',
        label: getDetailLabel('metrics'),
        defaultVisible: true,
        userVisible: true,
      },
      {
        field: 'actions',
        label: getDetailLabel('actions'),
        defaultVisible: true,
        userVisible: true,
      },
    ],
    defaultSortKey: testDefaultSortKey,
    defaultSortDirection: testDefaultSortDirection,
    sortOptions: testSortOptions,
    quickSortOptions: testQuickSortOptions,
    sortFieldMap: testSortFieldMap,
    statusOptions: testStatusOptions,
  });
